import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useAPI } from "../../contexts/APIContext";
import TrialWall from "../TrialWall";
import logger from "../../utils/logger";
// The original kit pieces: this welcome is laid out on them and is live
import { FREDOKA, NO, StepHeading, ContinueButton } from "./NOKitLegacy";
import { ADVENTURES } from "./steps/AdventureStep";

// Onboarding for families who arrive from make.pixleyai.com (a paid one-off occasion video). The backend seeds
// users.persona.make at purchase (core/make_orders._seed_persona), which also skips the generic onboarding:
// we already know the character, its name and what it loves. So instead of seven questions:
//   1. welcome   their character + the video they bought (saved here) + what else Pixley makes
//   2. next      "What should <star> do next?", ideas seeded from what they love → the first episode
//   3. paywall   the mandatory membership wall (make buyers see the make offer, see core/config.make_offer_*)
// The pick is stashed as pixley_first_story_pending, which EntitlementGateContext turns into a story the moment
// the membership is active.
// make.status "offer": they took the iPhone offer ("get this video free with a Pixley trial") but signed in here.
// OfferWelcome shows the held video → the membership wall → POST /make/claim-free once they're a member.

export const makeWelcomePending = (user) => {
  let persona = user?.persona;
  if (typeof persona === "string") {
    try {
      persona = JSON.parse(persona);
    } catch {
      return null;
    }
  }
  const make = persona && typeof persona === "object" ? persona.make : null;
  return make && (make.status === "pending" || make.status === "offer") ? { persona, make } : null;
};

const OCCASION_TITLES = {
  "birthday-invite": "birthday invite", "happy-birthday": "birthday", "new-year": "New Year",
  "lunar-new-year": "Lunar New Year", valentines: "Valentine's Day", "mothers-day": "Mother's Day",
  "fathers-day": "Father's Day", "raksha-bandhan": "Raksha Bandhan", "first-day-of-school": "first day of school",
  "new-sibling": "new sibling", "lost-tooth": "lost tooth", "thank-you-teacher": "teacher thank-you",
  "thank-you": "thank-you", "get-well": "get-well",
};
const occasionName = (key) => OCCASION_TITLES[key]
  || (key || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Ready-made first episodes: the onboarding adventure art, written for their star.
const adventureIdeas = (star) => [
  { ...ADVENTURES[0], idea: `${star} finds an old treasure map and sets sail to find the pirate gold` },
  { ...ADVENTURES[1], idea: `${star} blasts off on a rocket to help a lost little star find its way home` },
  { ...ADVENTURES[2], idea: `${star} discovers a dinosaur egg that is about to hatch` },
  { ...ADVENTURES[3], idea: `${star} dives under the sea to find the singing whale` },
];
// …and from what they told us they love on make ("dinosaurs, soccer and pancakes").
const loveIdeas = (star, loves) => (loves || []).slice(0, 3).map((love, i) => {
  const l = love.toLowerCase();
  return [
    `${star} discovers a secret world full of ${l}`,
    `${star} has the best day ever, all about ${l}`,
    `${star} goes on a big adventure with ${l}`,
  ][i % 3];
});

const FEATURES = (star) => [
  { img: "/assets/cancel/try_classic.webp", title: "New episodes",
    line: `Animated stories starring ${star}, made from any idea you have.` },
  { img: "/assets/landing/landing-storybook.jpeg", title: "Storybooks",
    line: `Illustrated picture books with ${star} as the hero, read aloud.` },
  { img: "/assets/cancel/try_learn.webp", title: "Learning games",
    line: "Letters, counting and writing games to play together." },
];

function Shell({ children }) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)" }}>
      <div className="min-h-full w-full flex items-center justify-center px-4 py-10">{children}</div>
    </div>
  );
}

function VideoCard({ src, poster }) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  return (
    <div className="relative mx-auto" style={{ width: "min(300px, 72vw)", aspectRatio: "9 / 16", borderRadius: 34, background: NO.ink, padding: 8, boxShadow: "0 40px 70px -34px rgba(11,26,51,.6)" }}>
      {src ? (
        <video ref={ref} src={src} poster={poster} autoPlay muted={muted} loop playsInline
          style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 27, display: "block", background: NO.ink }} />
      ) : (
        poster && <img src={poster} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 27 }} />
      )}
      {src && (
        <button type="button" onClick={() => { setMuted((m) => !m); ref.current?.play().catch(() => {}); }}
          style={{ ...FREDOKA, position: "absolute", left: "50%", bottom: 22, transform: "translateX(-50%)", border: 0, borderRadius: 999, padding: "8px 14px", background: "rgba(255,255,255,.94)", color: NO.ink, fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}>
          {muted ? "🔇 Tap for sound" : "🔊 Sound on"}
        </button>
      )}
    </div>
  );
}

function OfferWelcome({ make, persona }) {
  const { hasEntitlement } = useAuth();
  const { apiClient } = useAPI();
  const [state, setState] = useState(null);       // GET /make/me
  const [screen, setScreen] = useState("loading");
  const claimed = useRef(false);
  const star = (make.star_name || persona.child_name || "your character").trim();
  const who = (persona.child_name || star).trim();       // whose video it is (a Pixley character can star in it)

  const done = async () => {
    try {
      await apiClient.request("/make/me/done", { method: "POST" });
    } catch (e) {
      logger.warn("make offer: done failed", e);
    }
    window.location.href = "/library";
  };

  useEffect(() => {
    if (!apiClient) return;
    apiClient.request("/make/me").then((r) => {
      const st = r?.data || null;
      if (!st || st.kind !== "trial_offer" || st.status === "expired") {
        done();                                    // nothing waiting any more: the usual app (and its wall)
        return;
      }
      setState(st);
      setScreen("offer");
    }).catch((e) => { logger.warn("make offer: state failed", e); setScreen("offer"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient]);

  // a member now (back from checkout, or already subscribed): make the video
  useEffect(() => {
    if (!hasEntitlement || screen === "loading" || claimed.current || !apiClient) return;
    claimed.current = true;
    setScreen("claiming");
    // RevenueCat can take a few seconds to show a membership that was just bought
    (async () => {
      for (let attempt = 0; attempt < 5; attempt += 1) {
        if (attempt) await new Promise((r) => setTimeout(r, attempt * 1500));
        try {
          await apiClient.request("/make/claim-free", { method: "POST" });
          setScreen("claimed");
          return;
        } catch (e) {
          logger.warn("make offer: claim attempt failed", e);
        }
      }
      setScreen("claim_failed");
    })();
  }, [hasEntitlement, screen, apiClient]);

  const occasion = state?.occasion_title || occasionName(make.occasion);
  if (screen === "paywall") {
    return (
      <div className="fixed inset-0 z-50" style={{ background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)" }}>
        <TrialWall isOpen onClose={() => {}}
          context={{ trigger: "make_onboarding", mandatory: true, star: who, offerVideo: occasion }} />
      </div>
    );
  }
  const copy = {
    loading: null,
    offer: { eyebrow: "Saved for you", title: `${who}'s ${occasion} video `, accent: "is waiting",
      subtitle: `Become a Pixley member and we'll make it right away, free. Then keep going: new episodes, storybooks and learning games starring ${star}.`,
      cta: `Get ${who}'s video free`, onClick: () => setScreen("paywall") },
    claiming: { eyebrow: "One moment", title: `Starting ${who}'s video`, accent: "…", subtitle: "" },
    claimed: { eyebrow: "On its way", title: `${who}'s ${occasion} video `, accent: "is being made",
      subtitle: "It takes about 10 minutes. It'll be in your library, and we'll email you when it's ready.",
      cta: "Go to my library", onClick: done },
    claim_failed: { eyebrow: "Almost there", title: "We couldn't start the video ", accent: "yet",
      subtitle: "Your membership is active, but we couldn't confirm it just now. Email team@pixleyai.com and we'll make it for you.",
      cta: "Continue to Pixley", onClick: done },
  }[screen];
  if (!copy) return <Shell><div className="w-10 h-10 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin" /></Shell>;
  return (
    <Shell>
      <div className="w-full max-w-5xl animate-fadeIn">
        <div className="grid gap-10 items-center" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
          <VideoCard poster={state?.scene_url || state?.star_url || ""} />
          <div>
            <StepHeading eyebrow={copy.eyebrow} title={copy.title} accent={copy.accent} subtitle={copy.subtitle} />
            {copy.cta && (
              <div className="mt-8 flex justify-center sm:justify-start">
                <ContinueButton onClick={copy.onClick} label={copy.cta} />
              </div>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}

export default function MakeWelcome({ make, persona }) {
  if (make.status === "offer") return <OfferWelcome make={make} persona={persona} />;
  return <PaidWelcome make={make} persona={persona} />;
}

function PaidWelcome({ make, persona }) {
  const { user, hasEntitlement } = useAuth();
  const { apiClient } = useAPI();
  const navigate = useNavigate();
  const [screen, setScreen] = useState("welcome");
  const [video, setVideo] = useState({ src: make.preview_video || "", poster: make.preview_poster || "" });   // preview_video: dev preview only
  const [pick, setPick] = useState(null);       // an idea string
  const [own, setOwn] = useState("");
  const [saving, setSaving] = useState(false);
  const star = (make.star_name || persona.child_name || "your character").trim();
  const occasion = occasionName(make.occasion);

  // the welcome sits over /library (where their video lives), whatever page sign-in returned them to
  useEffect(() => {
    if (window.location.pathname !== "/library") navigate("/library", { replace: true });
  }, [navigate]);

  // a refunded order (make.video_failed): no video to show, so show their character instead
  useEffect(() => {
    if (!make.video_failed || !apiClient || !user?.id) return;
    apiClient.getUserCharacters(user.id).then((r) => {
      const c = ((r?.status && r.data) || []).find((x) => x.id === make.character_id);
      const img = c?.animated_image_url || c?.image_url;
      if (img) setVideo({ src: "", poster: img });
    }).catch(() => {});
  }, [apiClient, user?.id, make.video_failed, make.character_id]);

  useEffect(() => {
    if (make.video_failed || !make.story_id || !apiClient) return;
    let alive = true;
    apiClient.getStory(make.story_id).then((r) => {
      const s = r?.data || r || {};
      if (alive) setVideo({ src: s.file_url || s.video_url || "", poster: s.thumbnail_url || s.cover_image_url || s.thumbnail || "" });
    }).catch((e) => logger.warn("make welcome: story load failed", e));
    return () => { alive = false; };
  }, [apiClient, make.story_id, make.video_failed]);

  const ideas = useMemo(() => adventureIdeas(star), [star]);
  const lovePicks = useMemo(() => loveIdeas(star, make.loves), [star, make.loves]);
  const chosen = own.trim().length >= 3 ? own.trim() : pick;

  const toPaywall = async () => {
    if (!chosen || saving) return;
    setSaving(true);
    try {
      localStorage.setItem("pixley_first_story_pending", JSON.stringify({
        idea: chosen, savedAt: Date.now(), character_id: make.character_id, character_name: star,
      }));
      // reloads before paying land back on the wall (Navigation reads this flag), not on this welcome
      localStorage.setItem("pixley_show_subscription_cta", "true");
    } catch (_) { /* storage blocked: the wall still shows now */ }
    try {
      // done server-side only: the local user keeps "pending" so this screen stays up to show the wall
      await apiClient.updateUserPersona(user.id, { ...persona, make: { ...make, status: "done", first_idea: chosen } });
    } catch (e) {
      logger.warn("make welcome: persona update failed", e);
    }
    setSaving(false);
    if (hasEntitlement) {
      window.location.href = "/library";
      return;
    }
    setScreen("paywall");
  };

  if (screen === "paywall") {
    return (
      <div className="fixed inset-0 z-50" style={{ background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)" }}>
        <TrialWall isOpen onClose={() => {}} context={{ trigger: "make_onboarding", mandatory: true, star }} />
      </div>
    );
  }

  if (screen === "next") {
    return (
      <Shell>
        <div className="w-full max-w-2xl animate-fadeIn">
          <StepHeading eyebrow="Your first episode" title={`What should ${star} do `} accent="next?"
            subtitle="Pick one, or write your own. It's the first thing we'll make." />
          <div className="grid grid-cols-2 gap-3 mt-6">
            {ideas.map((a) => {
              const on = pick === a.idea && !own.trim();
              return (
                <button key={a.id} type="button" onClick={() => { setPick(a.idea); setOwn(""); }}
                  className="text-left transition active:scale-[0.98]"
                  style={{ borderRadius: 22, overflow: "hidden", background: "#fff", border: `2.5px solid ${on ? NO.accent : NO.cardStroke}`, boxShadow: on ? "0 12px 26px -14px rgba(0,101,248,.55)" : "none", cursor: "pointer", padding: 0 }}>
                  <img src={a.thumb} alt="" style={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", display: "block" }} />
                  <div style={{ ...FREDOKA, padding: "10px 14px 12px", color: NO.ink, fontWeight: 600, fontSize: 15, lineHeight: 1.3 }}>{a.idea}</div>
                </button>
              );
            })}
          </div>
          {lovePicks.length > 0 && (
            <>
              <div style={{ ...FREDOKA, color: NO.subtitle, fontWeight: 600, fontSize: 14, margin: "22px 0 10px" }}>
                Made from what {star} loves
              </div>
              <div className="flex flex-col gap-2">
                {lovePicks.map((idea) => {
                  const on = pick === idea && !own.trim();
                  return (
                    <button key={idea} type="button" onClick={() => { setPick(idea); setOwn(""); }}
                      className="text-left transition active:scale-[0.99]"
                      style={{ ...FREDOKA, borderRadius: 16, padding: "13px 16px", background: on ? "#EAF2FF" : "#fff", border: `2px solid ${on ? NO.accent : NO.cardStroke}`, color: NO.ink, fontWeight: 600, fontSize: 15.5, cursor: "pointer" }}>
                      ✨ {idea}
                    </button>
                  );
                })}
              </div>
            </>
          )}
          <input value={own} onChange={(e) => setOwn(e.target.value.slice(0, 200))} placeholder={`Or type your own idea for ${star}…`}
            style={{ ...FREDOKA, width: "100%", marginTop: 14, borderRadius: 16, padding: "14px 16px", fontSize: 16, border: `2px solid ${own.trim() ? NO.accent : NO.cardStroke}`, background: "#fff", color: NO.ink, outline: "none" }} />
          <div className="flex items-center justify-between mt-6">
            <button type="button" onClick={() => setScreen("welcome")} style={{ ...FREDOKA, border: 0, background: "none", color: NO.subtitle, fontWeight: 600, fontSize: 15, cursor: "pointer" }}>← Back</button>
            <ContinueButton onClick={toPaywall} disabled={!chosen} loading={saving} label="Make this episode" />
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="w-full max-w-5xl animate-fadeIn">
        <div className="grid gap-10 items-center" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
          <VideoCard src={video.src} poster={video.poster} />
          <div>
            <StepHeading eyebrow="Welcome to Pixley" title={`${star} `} accent="lives here now"
              subtitle={make.video_failed
              ? `We couldn't finish your ${occasion} video and refunded you in full. But ${star} is all set up here, ready for a first episode.`
              : `Your ${occasion} video is saved in your library. Rewatch it, download it and share it anytime.`} />
            <div className="mt-8 flex justify-center sm:justify-start">
              <ContinueButton onClick={() => setScreen("next")} label={`What should ${star} do next?`} />
            </div>
          </div>
        </div>

        <section className="mt-16">
          <h2 style={{ ...FREDOKA, color: NO.ink, fontWeight: 700, fontSize: "clamp(22px, 2.6vw, 28px)", margin: "0 0 18px" }}>
            And {star} can star in so much more
          </h2>
          <div className="grid gap-5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))" }}>
            {FEATURES(star).map((f) => (
              <div key={f.title} style={{ background: "#fff", border: `1.5px solid ${NO.cardStroke}`, borderRadius: 26, overflow: "hidden", boxShadow: "0 22px 44px -34px rgba(11,26,51,.55)" }}>
                <img src={f.img} alt="" loading="lazy" style={{ width: "100%", aspectRatio: "16 / 10", objectFit: "cover", display: "block" }} />
                <div style={{ padding: "18px 20px 22px" }}>
                  <div style={{ ...FREDOKA, color: NO.ink, fontWeight: 700, fontSize: 19, marginBottom: 6 }}>{f.title}</div>
                  <div style={{ ...FREDOKA, color: NO.subtitle, fontSize: 15, lineHeight: 1.45 }}>{f.line}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Shell>
  );
}
